/**
 * Shared Supabase access for the data layer.
 *
 * Only files inside `src/data/` should import from here. Components and
 * pages must use a hook from `src/data/queries/` or `src/data/mutations/`.
 */
import { supabase } from "@/integrations/supabase/client";
import type { PostgrestError } from "@supabase/supabase-js";

export { supabase };

/** Normalized error shape returned to query/mutation hooks. */
export class DataError extends Error {
  code?: string;
  details?: string;
  hint?: string;

  constructor(message: string, opts?: { code?: string; details?: string; hint?: string }) {
    super(message);
    this.name = "DataError";
    this.code = opts?.code;
    this.details = opts?.details;
    this.hint = opts?.hint;
  }
}

/**
 * Unwrap a Supabase `{ data, error }` response.
 * Throws a `DataError` on failure so React Query routes it through `error`.
 */
export function unwrap<T>(res: { data: T | null; error: PostgrestError | null }): T {
  if (res.error) {
    throw new DataError(res.error.message, {
      code: res.error.code,
      details: res.error.details,
      hint: res.error.hint,
    });
  }
  if (res.data === null) {
    throw new DataError("No data returned");
  }
  return res.data;
}

/** Same as `unwrap` but allows `null` (e.g. `.maybeSingle()` results). */
export function unwrapMaybe<T>(res: { data: T | null; error: PostgrestError | null }): T | null {
  if (res.error) {
    throw new DataError(res.error.message, {
      code: res.error.code,
      details: res.error.details,
      hint: res.error.hint,
    });
  }
  return res.data;
}
